import React, { useCallback } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import { FormattedMessage, useIntl } from 'react-intl';
import { map } from 'lodash';
import { CircularProgress } from '@material-ui/core';
import styles from './manageCustomers.styles';
import { Typography } from '../../components/Typography';
import { Card } from '../../components/Card';
import { Table } from '../../components/Table';
import { Button } from '../../components/Button';
import { IGetMemberResponse, IMembersState } from '../members/members.slice';
import { IRequestParams } from '../../components/types';
import { getUserStatus } from '../../helper/translationFunctions';

interface IMemberDataCardProps extends WithStyles<typeof styles> {
  selectMember: IMembersState;
  membersList: IGetMemberResponse;
  handleDeleteMember: (data: string) => void;
  handleGetMembers: ({ limit, offset, sortOrder }: IRequestParams) => void;
  onAddMember: () => void;
  onRemoveMember: (name: string, callback: () => void) => void;
}

const MemberDataCard: React.FC<IMemberDataCardProps> = ({
  classes,
  selectMember,
  membersList,
  handleDeleteMember,
  handleGetMembers,
  onAddMember,
  onRemoveMember,
}) => {
  const intl = useIntl();

  const membersTableHeadings = [
    {
      name: 'name',
      label: intl.formatMessage({
        id: 'name',
      }),
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'email',
      label: intl.formatMessage({
        id: 'email',
      }),
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'status',
      label: intl.formatMessage({
        id: 'status',
      }),
      options: {
        filter: false,
        sort: false,
      },
    },
    {
      name: 'action',
      label: intl.formatMessage({
        id: 'action',
      }),
      options: {
        filter: false,
        sort: false,
      },
    },
  ];

  const renderMemberTableRow = useCallback(() => {
    return map(membersList.data, (row) => {
      const memberName = row.profile.name;
      const memberId = row._id;
      return {
        ...row,
        name: row.profile.name,
        status: getUserStatus(intl, row.status, true),
        action: (
          <IconButton
            aria-label={intl.formatMessage({
              id: 'remove',
            })}
            color="inherit"
            size="small"
            onClick={() =>
              onRemoveMember(memberName, () => {
                handleDeleteMember(memberId);
              })
            }
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        ),
      };
    });
  }, [intl, membersList, handleDeleteMember, onRemoveMember]);

  return (
    <Card>
      <div className={classes.cardContentWrapper}>
        <div style={{ float: 'right' }}>
          <Button
            id="add-member"
            label={intl.formatMessage({
              id: 'add_member',
            })}
            variant="contained"
            onClick={onAddMember}
          />
        </div>
        <Table
          customStyles={classes.tableWrapper}
          id="member-data-table"
          title={
            <>
              <Typography variant="h6" className={classes.cardTitle}>
                <FormattedMessage id="members" />
                {selectMember.loading && (
                  <CircularProgress
                    size={24}
                    style={{ marginLeft: 15, position: 'relative', top: 4 }}
                  />
                )}
              </Typography>
            </>
          }
          columns={membersTableHeadings}
          data={renderMemberTableRow()}
          pagination
          serverSide
          filter={false}
          count={membersList.total}
          dataRefreshFunc={handleGetMembers}
        />
      </div>
    </Card>
  );
};

export default withStyles(styles)(MemberDataCard);
